import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type Locale = 'ru' | 'kk';
export type LocalizedText = Record<Locale, string>;

const messages = {
  ru: {
    home: 'Главная',
    about: 'Обо мне',
    lessons: 'Уроки',
    quizzes: 'Тесты',
    blog: 'Статьи',
    contact: 'Связаться',
    viewLessons: 'Смотреть уроки',
    takeQuiz: 'Пройти тест',
    allLessons: 'Все уроки',
    allQuizzes: 'Все тесты',
    read: 'Читать',
    menu: 'Меню',
    close: 'Закрыть',
    language: 'Язык',
    demo: 'Демонстрационный материал',
    contentPending: 'Материал будет опубликован после подготовки контента.',
  },
  kk: {
    home: 'Басты бет',
    about: 'Мен туралы',
    lessons: 'Сабақтар',
    quizzes: 'Тесттер',
    blog: 'Мақалалар',
    contact: 'Байланысу',
    viewLessons: 'Сабақтарды көру',
    takeQuiz: 'Тест тапсыру',
    allLessons: 'Барлық сабақтар',
    allQuizzes: 'Барлық тесттер',
    read: 'Оқу',
    menu: 'Мәзір',
    close: 'Жабу',
    language: 'Тіл',
    demo: 'Демонстрациялық материал',
    contentPending: 'Материал контент дайындалғаннан кейін жарияланады.',
  },
} as const;

type MessageKey = keyof (typeof messages)['ru'];

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey) => string;
  localize: (value: LocalizedText) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function getInitialLocale(): Locale {
  const stored = window.localStorage.getItem('ustaz-locale');
  return stored === 'kk' ? 'kk' : 'ru';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (nextLocale) => {
        document.documentElement.lang = nextLocale;
        window.localStorage.setItem('ustaz-locale', nextLocale);
        setLocaleState(nextLocale);
      },
      t: (key) => messages[locale][key],
      localize: (text) => text[locale],
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used inside LocaleProvider');
  }
  return context;
}
